/** Auth navigation and presentation. Never persist passwords or reset tokens. */
import { auth } from "./auth-client";
import { api, ApiError } from "./api";
import { object, type Viewer } from "./core/workspace";
import { copy } from "./session";
import { action, feedback, field, form, hint, node, submit, icon, errorText } from "./account-ui";

type Mode = "signin" | "signup" | "recovery";
type Sent = "verification" | "recovery";
const paths: Record<Mode, string> = { signin: "/login", signup: "/signup", recovery: "/forgot-password" };
let emailValue = "";
let activeRoot: HTMLElement | null = null;

function move(mode: Mode): void {
  history.pushState({}, "", paths[mode]); if (activeRoot) renderAuth(activeRoot, true);
}
function route(label: string, mode: Mode, className = "text-link"): HTMLAnchorElement {
  const link = node("a", label, className); link.href = paths[mode];
  link.onclick = (event) => { if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); move(mode); } };
  return link;
}
export function authFrame(root: HTMLElement, card: HTMLElement, focus = false): void {
  root.className = "auth-workspace";
  const intro = node("aside", undefined, "auth-intro");
  intro.append(node("p", copy().byServarium, "auth-byline"));
  const headline = node("p", undefined, "auth-headline"); headline.append(node("span", copy().intro), node("span", copy().introAccent, "caption-accent"));
  intro.append(headline, node("p", copy().introText, "auth-description"));
  const details = node("div", undefined, "auth-details");
  details.append(node("p", copy().localVideo), node("p", copy().privateDrafts)); intro.append(details);
  const panel = node("div", undefined, "auth-panel"); panel.append(card, node("p", copy().privacy, "auth-privacy")); root.replaceChildren(intro, panel);
  if (focus) card.querySelector<HTMLElement>("h1")?.focus();
}
function secondary(card: ReturnType<typeof form>, label: string, work: () => Promise<void>, primary = false): HTMLButtonElement {
  return action(label, async () => {
    feedback(card.status, copy().processing);
    try { await work(); } catch (error) { feedback(card.status, errorText(error), "error"); }
  }, primary);
}
async function verification(): Promise<void> {
  await api("/api/auth/send-verification-email", "POST", { email: emailValue, callbackURL: `${location.origin}/projects` });
}
function sent(root: HTMLElement, kind: Sent, focus: boolean): void {
  const card = form(copy().checkEmail, kind === "verification" ? copy().verificationSent : copy().recoverySent);
  card.form.remove();
  if (emailValue) {
    const address = node("div", undefined, "email-destination"); address.append(node("span", copy().emailDestination, "hint"), node("strong", emailValue)); card.status.before(address);
  }
  card.status.before(node("p", kind === "verification" ? copy().verificationNext : copy().emailHelp, "form-intro"));
  const resend = secondary(card, copy().resend, async () => {
    if (kind === "verification") await verification();
    else await api("/api/auth/request-password-reset", "POST", { email: emailValue, redirectTo: `${location.origin}/reset-password` });
    feedback(card.status, copy().emailResent, "success");
  });
  if (emailValue) card.container.append(resend);
  const footer = node("div", undefined, "auth-footer"); footer.append(route(copy().editEmail, kind === "verification" ? "signup" : "recovery"), route(copy().toSignIn, "signin")); card.container.append(footer);
  authFrame(root, card.container, focus);
}
function markSent(root: HTMLElement, kind: Sent): void {
  history.pushState({ kind, email: emailValue }, "", "/check-email"); sent(root, kind, true);
}
function invalidReset(root: HTMLElement, focus = false): void {
  // Invalid or spent secrets must not remain in the address bar/history entry.
  history.replaceState({}, "", "/reset-password");
  const card = form(copy().invalidResetTitle, copy().invalidReset); card.form.remove(); card.status.remove();
  card.container.append(route(copy().newReset, "recovery", "primary button-link wide"), route(copy().toSignIn, "signin", "text-link auth-back"));
  authFrame(root, card.container, focus);
}
function reset(root: HTMLElement, focus: boolean): void {
  const token = new URL(location.href).searchParams.get("token");
  if (!token || new URL(location.href).searchParams.has("error")) { invalidReset(root, focus); return; }
  const card = form(copy().resetPassword, copy().resetText);
  const password = field(copy().newPassword, "password", "new-password"); password.input.minLength = 12; card.form.append(password.label, hint(password.input, copy().passwordHint));
  submit(card.form, copy().resetPassword, card.status, async () => {
    await api("/api/auth/reset-password", "POST", { token, newPassword: password.input.value });
    history.replaceState({}, "", "/login");
    const done = form(copy().resetComplete, copy().resetCompleteText); done.form.remove(); done.status.remove();
    done.container.append(route(copy().signIn, "signin", "primary button-link wide")); authFrame(root, done.container, true);
  }, (error) => {
    const code = error instanceof ApiError ? object(error.payload)?.code : null;
    if (code === "INVALID_TOKEN" || code === "TOKEN_EXPIRED") invalidReset(root, true);
  });
  card.container.append(route(copy().toSignIn, "signin", "text-link auth-back")); authFrame(root, card.container, focus);
}
export function renderAuth(root: HTMLElement, focus = false): void {
  activeRoot = root;
  if (location.pathname === "/reset-password") { reset(root, focus); return; }
  if (location.pathname === "/check-email") {
    const state = object(history.state);
    if (state?.kind === "verification" || state?.kind === "recovery") {
      emailValue = typeof state.email === "string" ? state.email : emailValue; sent(root, state.kind, focus); return;
    }
    history.replaceState({}, "", "/login");
  }
  const mode: Mode = location.pathname === "/signup" ? "signup" : location.pathname === "/forgot-password" ? "recovery" : "signin";
  const words = copy();
  const card = form(mode === "signin" ? words.signInTitle : mode === "signup" ? words.signUp : words.recoveryTitle,
    mode === "signin" ? words.signInText : mode === "signup" ? words.signUpText : words.recoveryText);
  const email = field(words.email, "email", mode === "signin" ? "username webauthn" : "email", emailValue);
  email.input.oninput = () => { emailValue = email.input.value; }; card.form.append(email.label);
  let password: ReturnType<typeof field> | null = null, name: ReturnType<typeof field> | null = null;
  if (mode === "signup") { name = field(words.name, "text", "name"); name.input.maxLength = 100; card.form.append(name.label); }
  if (mode !== "recovery") {
    password = field(words.password, "password", mode === "signin" ? "current-password webauthn" : "new-password");
    password.input.minLength = mode === "signup" ? 12 : 1; card.form.append(password.label);
    if (mode === "signup") card.form.append(hint(password.input, words.passwordHint));
    else card.form.append(route(words.forgotten, "recovery", "text-link forgot-link"));
  }
  const resend = secondary(card, words.resend, async () => {
    if (!email.input.reportValidity()) { feedback(card.status, ""); return; }
    emailValue = email.input.value; await verification(); markSent(root, "verification");
  }); resend.hidden = true;
  submit(card.form, mode === "signin" ? words.signIn : mode === "signup" ? words.signUp : words.sendReset, card.status, async () => {
    const address = email.input.value.trim(); emailValue = address;
    if (mode === "recovery") await api("/api/auth/request-password-reset", "POST", { email: address, redirectTo: `${location.origin}/reset-password` });
    else if (mode === "signup") await api("/api/auth/sign-up/email", "POST", { email: address, name: name?.input.value, password: password?.input.value, callbackURL: `${location.origin}/projects` });
    else await api("/api/auth/sign-in/email", "POST", { email: address, password: password?.input.value });
    if (!card.container.isConnected) return;
    emailValue = address;
    if (mode === "signin") location.replace("/projects"); else markSent(root, mode === "signup" ? "verification" : "recovery");
  }, (error) => { resend.hidden = !(error instanceof ApiError && object(error.payload)?.code === "EMAIL_NOT_VERIFIED"); });
  card.container.append(resend);
  if (mode === "signin") {
    card.container.append(node("div", words.or, "auth-divider"));
    const passkey = action(words.passkey, async () => {
      feedback(card.status, words.processing);
      try { const result = await auth.signIn.passkey(); if (result.error) { feedback(card.status, words.passkeyFailed, "error"); return; } location.replace("/projects"); }
      catch { feedback(card.status, words.passkeyFailed, "error"); }
    }); passkey.classList.add("passkey-button"); passkey.prepend(icon("key"));
    card.container.append(passkey, node("p", words.passkeyHelp, "hint"));
  }
  const footer = node("div", undefined, "auth-footer");
  if (mode === "recovery") footer.append(route(words.toSignIn, "signin"));
  else footer.append(node("span", mode === "signin" ? words.noAccount : words.hasAccount), route(mode === "signin" ? words.signUp : words.signIn, mode === "signin" ? "signup" : "signin"));
  card.container.append(footer); authFrame(root, card.container, focus);
}
window.addEventListener("popstate", () => { if (activeRoot?.isConnected) renderAuth(activeRoot, true); });
export function renderAccess(root: HTMLElement, viewer: Viewer): void {
  const card = form(viewer.status === "pending" ? copy().pendingTitle : copy()[viewer.status === "rejected" ? "rejected" : "suspended"], viewer.status === "pending" ? copy().pendingText : copy().accessHelp);
  card.form.remove(); card.status.remove();
  card.container.append(node("p", viewer.email, "access-email"), action(copy().refresh, () => location.reload(), true));
  authFrame(root, card.container);
}
